/**
 * @fileoverview User directory filter options query hook.
 * @description Wraps GET /users/filter-options for a table's Role, Reporting Manager and State
 * filters — the User Management directory (DIRECTORY) or the Sales Team page (MY_TEAM). The Status
 * filter's choices are fixed (Pending, Active, Inactive), so they aren't fetched.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import {
  getManagerFilterOptions,
  getRoleFilterOptions,
  getStateFilterOptions,
} from '@/features/users/services/users.service'

import type {
  ReportingManagerOption,
  RoleOption,
  StateFilterOption,
  UserFilterOptionsContext,
} from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

const NO_ROLES: RoleOption[] = []
const NO_MANAGERS: ReportingManagerOption[] = []
const NO_STATES: StateFilterOption[] = []

/**
 * @description Fetches the Role, Reporting Manager and State filter choices.
 * @param {UserFilterOptionsContext} [context] - The table (default DIRECTORY).
 * @param {boolean} [withStates] - Whether to fetch the State choices too (only the User
 * Management directory has a State filter; default false).
 * @returns {{ roles: RoleOption[]; managers: ReportingManagerOption[]; states: StateFilterOption[]
 * }} Each filter's choices, empty until loaded or when the request fails (the filter then offers
 * only "All").
 */
export function useUserFilterOptions(
  context: UserFilterOptionsContext = 'DIRECTORY',
  withStates = false,
) {
  const rolesQuery = useQuery<RoleOption[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.filterOptions, context, 'ROLE'],
    queryFn: () => getRoleFilterOptions(context),
  })
  const managersQuery = useQuery<ReportingManagerOption[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.filterOptions, context, 'REPORTING_MANAGER'],
    queryFn: () => getManagerFilterOptions(context),
  })
  const statesQuery = useQuery<StateFilterOption[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.filterOptions, context, 'STATE'],
    queryFn: () => getStateFilterOptions(context),
    enabled: withStates,
  })

  return {
    roles: rolesQuery.data ?? NO_ROLES,
    managers: managersQuery.data ?? NO_MANAGERS,
    states: statesQuery.data ?? NO_STATES,
  }
}
